/**
 * Lobby emote choreography hook-in point (docs/ASSETS.md §4). The placeholder animation is a CSS bounce on
 * the actor's `CharacterBadge`; replace `EMOTE_ANIMATIONS[id].className` with finished choreography per
 * emote ID. Under `prefers-reduced-motion`, `Lobby.tsx` shows the `verb` as a 2s textual badge instead of
 * playing the animation.
 */
import { useEffect, useRef, useState } from 'react';
import { LobbyEmoteEvent, type EmoteId } from '@momentum/shared';
import type { SessionClient } from '../session/client.ts';

export interface EmoteAnimation {
  /** CSS class applied to the actor's CharacterBadge for `durationMs`. */
  className: string;
  durationMs: number;
  /** Past-tense verb shown as text (used always, and standing in for motion under reduced motion). */
  verb: string;
}

export const EMOTE_ANIMATIONS: Record<EmoteId, EmoteAnimation> = {
  wave: { className: 'emote-bounce', durationMs: 2000, verb: 'waved' },
  bounce: { className: 'emote-bounce', durationMs: 2000, verb: 'bounced' },
  spin: { className: 'emote-bounce', durationMs: 2000, verb: 'spun' },
  cheer: { className: 'emote-bounce', durationMs: 2000, verb: 'cheered' },
};

export interface ActiveEmote {
  emoteId: EmoteId;
}

/** Tracks the currently-animating emote per actor ID, clearing each after its animation duration. */
export function useLobbyEmotes(client: SessionClient): Record<string, ActiveEmote> {
  const [active, setActive] = useState<Record<string, ActiveEmote>>({});
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const unsubscribe = client.onEvent((event) => {
      if (event.name !== 'lobby.emote') return;
      const parsed = LobbyEmoteEvent.safeParse(event.payload);
      if (!parsed.success) return;
      const { actorId, emoteId } = parsed.data;
      const animation = EMOTE_ANIMATIONS[emoteId];

      setActive((prev) => ({ ...prev, [actorId]: { emoteId } }));

      const existing = timers.current.get(actorId);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        setActive((prev) => {
          if (!(actorId in prev)) return prev;
          const next = { ...prev };
          delete next[actorId];
          return next;
        });
        timers.current.delete(actorId);
      }, animation.durationMs);
      timers.current.set(actorId, timer);
    });

    const timersMap = timers.current;
    return () => {
      unsubscribe();
      for (const timer of timersMap.values()) clearTimeout(timer);
      timersMap.clear();
    };
  }, [client]);

  return active;
}
